import { useCallback, useEffect, useRef, useState } from 'react';

export const TRANSIENT_MESSAGE_MS = 2500;

/**
 * Inline gameplay status line that auto-clears after a short timeout.
 * Empty string clears immediately (use on reset / new game).
 */
export function useTransientMessage(durationMs = TRANSIENT_MESSAGE_MS) {
    const [message, setMessageState] = useState('');
    const timerRef = useRef(null);

    const clearMessage = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setMessageState('');
    }, []);

    const setMessage = useCallback((next) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        const text = next == null ? '' : String(next);
        setMessageState(text);
        if (text) {
            timerRef.current = setTimeout(() => {
                setMessageState('');
                timerRef.current = null;
            }, durationMs);
        }
    }, [durationMs]);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    return [message, setMessage, clearMessage];
}

/**
 * Auto-clear `view.message` (or similar) after it stops changing.
 * Frequent identical updates do not re-arm the timer; new text does.
 */
export function useTransientViewMessage(message, setView, durationMs = TRANSIENT_MESSAGE_MS) {
    useEffect(() => {
        if (!message) return undefined;
        const timer = setTimeout(() => {
            setView((prev) => (prev?.message ? { ...prev, message: '' } : prev));
        }, durationMs);
        return () => clearTimeout(timer);
    }, [message, setView, durationMs]);
}
